import React from 'react';

import {
  AddressIcon,
  BankIcon,
  BuildingIcon,
  ChatIcon,
  DocumentIcon,
  DollarIcon,
  HomeIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';

import {
  fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
  PurposeOfChattel,
} from 'constants/loanApplication';
import { StatusColor, StatusEnum } from 'constants/status';
import { getConditionalArray } from 'utils/data';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  checkIsChattelCommunityPark,
  checkIsChattelLandContract,
  checkIsChattelLandownerPlacement,
  checkIsChattelLeasedPrivateProperty,
  getChattelSitePlacementLabel,
  getEffectiveChattelSitePlacement,
} from '../EditModal/Content/MainContent/ChattelProperty/ChattelProperty.config';

import {
  CHATTEL_FIELD_NAME,
  CHATTEL_GROUP_TITLE,
  CHATTEL_HOME_CONDITION_LABEL,
  CHATTEL_HOME_TYPE_LABEL,
  CHATTEL_VALUE_SOURCE_LABEL,
} from './ChattelDetailsSection.constants';
import { formatChattelSectionNumbers } from './ChattelDetailsSection.utils';

const formatCurrency = value =>
  formatNumberToCurrencyString(value ?? 0, fixedTwoFractionDigitsExceptIntegerCurrencyOptions);

// The home value with where it comes from, e.g. "$95,000 (NADA)"; just the amount when no source is recorded.
const formatHomeValue = (value, valueSource) => {
  const sourceLabel = valueSource ? (CHATTEL_VALUE_SOURCE_LABEL[valueSource] ?? valueSource) : null;

  return sourceLabel ? `${formatCurrency(value)} (${sourceLabel})` : formatCurrency(value);
};

// LEN-207: show only the location fields the placement uses -- the same ones the edit form asks for and the save
// keeps -- instead of a wall of "N/A" / "$0" (a park home listed Landowner Name and Monthly Land Payment). With no
// placement known, nothing is hidden.
const getLocationFieldVisibility = chattelDetails => {
  const sitePlacement = getEffectiveChattelSitePlacement(chattelDetails);
  const appliesTo = check => !sitePlacement || check(sitePlacement);

  return {
    isParkShown: appliesTo(checkIsChattelCommunityPark),
    isLotRentShown: appliesTo(
      placement =>
        checkIsChattelCommunityPark(placement) || checkIsChattelLeasedPrivateProperty(placement),
    ),
    isLandownerShown: appliesTo(checkIsChattelLandownerPlacement),
    isLandPaymentShown: appliesTo(checkIsChattelLandContract),
  };
};

export const getChattelGroupsConfig = chattelDetails => {
  const {
    make,
    model,
    serialNumber,
    serialNumber2,
    serialNumber3,
    hudLabelNumber,
    hudLabelNumber2,
    hudLabelNumber3,
    yearBuilt,
    homeType,
    condition,
    purposeOfChattel,
    estimatedCurrentValue,
    valueSource,
    originalPurchasePrice,
    salesPrice,
    salesTax,
    mhit,
    parkName,
    lotNumber,
    monthlyLotRent,
    landownerName,
    landownerPhone,
    landMonthlyPayment,
    locationDescription,
    dealershipName,
    dealershipAddress,
    dealershipPhone,
    dealershipEmail,
  } = chattelDetails ?? {};
  const { isParkShown, isLotRentShown, isLandownerShown, isLandPaymentShown } =
    getLocationFieldVisibility(chattelDetails);

  return [
    {
      title: CHATTEL_GROUP_TITLE.HOME_DETAILS,
      cards: [
        { name: CHATTEL_FIELD_NAME.MAKE, value: make, icon: <HomeIcon /> },
        { name: CHATTEL_FIELD_NAME.MODEL, value: model, icon: <HomeIcon /> },
        {
          name: CHATTEL_FIELD_NAME.SERIAL_NUMBER,
          value: formatChattelSectionNumbers([serialNumber, serialNumber2, serialNumber3]),
          icon: <DocumentIcon />,
          noTruncate: true,
        },
        {
          name: CHATTEL_FIELD_NAME.HUD_LABEL,
          value: formatChattelSectionNumbers([hudLabelNumber, hudLabelNumber2, hudLabelNumber3]),
          icon: <DocumentIcon />,
          noTruncate: true,
        },
        { name: CHATTEL_FIELD_NAME.YEAR_BUILT, value: yearBuilt, icon: <BuildingIcon /> },
        {
          name: CHATTEL_FIELD_NAME.HOME_TYPE,
          value: CHATTEL_HOME_TYPE_LABEL[homeType] ?? homeType,
          icon: <BuildingIcon />,
        },
        {
          name: CHATTEL_FIELD_NAME.CONDITION,
          value: CHATTEL_HOME_CONDITION_LABEL[condition] ?? condition,
          icon: <BuildingIcon />,
        },
      ],
    },
    {
      title: CHATTEL_GROUP_TITLE.VALUE,
      cards: [
        {
          // LEN-204: entered on Manufactured Home Details; a refinance is valued by a home-only appraisal.
          name:
            purposeOfChattel === PurposeOfChattel.REFINANCE
              ? CHATTEL_FIELD_NAME.APPRAISED_VALUE
              : CHATTEL_FIELD_NAME.HOME_VALUE,
          value: formatHomeValue(estimatedCurrentValue, valueSource),
          icon: <DollarIcon />,
          iconColor: StatusColor[StatusEnum.SUCCESS],
        },
        {
          name: CHATTEL_FIELD_NAME.ORIGINAL_PRICE,
          value: formatCurrency(originalPurchasePrice),
          icon: <DollarIcon />,
        },
      ],
    },
    {
      title: CHATTEL_GROUP_TITLE.SALES_CONTRACT,
      cards: [
        {
          name: CHATTEL_FIELD_NAME.SALES_PRICE,
          value: formatCurrency(salesPrice),
          icon: <DollarIcon />,
        },
        {
          name: CHATTEL_FIELD_NAME.SALES_TAX,
          value: formatCurrency(salesTax),
          icon: <DollarIcon />,
        },
        { name: CHATTEL_FIELD_NAME.MHIT, value: formatCurrency(mhit), icon: <DollarIcon /> },
      ],
    },
    {
      title: CHATTEL_GROUP_TITLE.LOCATION,
      cards: [
        {
          // LEN-207: the six-way placement the edit form captures, not the obsolete isInPark bit.
          name: CHATTEL_FIELD_NAME.SITE_PLACEMENT,
          value: getChattelSitePlacementLabel(chattelDetails),
          icon: <AddressIcon />,
        },
        ...getConditionalArray(isParkShown, [
          { name: CHATTEL_FIELD_NAME.PARK_NAME, value: parkName, icon: <AddressIcon /> },
          { name: CHATTEL_FIELD_NAME.LOT_NUMBER, value: lotNumber, icon: <AddressIcon /> },
        ]),
        ...getConditionalArray(isLotRentShown, [
          {
            name: CHATTEL_FIELD_NAME.MONTHLY_LOT_RENT,
            value: formatCurrency(monthlyLotRent),
            icon: <DollarIcon />,
            iconColor: StatusColor[StatusEnum.ERROR],
          },
        ]),
        ...getConditionalArray(isLandownerShown, [
          { name: CHATTEL_FIELD_NAME.LANDOWNER_NAME, value: landownerName, icon: <ChatIcon /> },
          { name: CHATTEL_FIELD_NAME.LANDOWNER_PHONE, value: landownerPhone, icon: <ChatIcon /> },
        ]),
        ...getConditionalArray(isLandPaymentShown, [
          {
            name: CHATTEL_FIELD_NAME.LAND_MONTHLY_PAYMENT,
            value: formatCurrency(landMonthlyPayment),
            icon: <DollarIcon />,
            iconColor: StatusColor[StatusEnum.ERROR],
          },
        ]),
        {
          name: CHATTEL_FIELD_NAME.LOCATION_DESCRIPTION,
          value: locationDescription,
          icon: <AddressIcon />,
        },
      ],
    },
    {
      title: CHATTEL_GROUP_TITLE.DEALERSHIP,
      cards: [
        { name: CHATTEL_FIELD_NAME.DEALERSHIP_NAME, value: dealershipName, icon: <BankIcon /> },
        {
          name: CHATTEL_FIELD_NAME.DEALERSHIP_ADDRESS,
          value: dealershipAddress,
          icon: <AddressIcon />,
        },
        {
          name: CHATTEL_FIELD_NAME.DEALERSHIP_PHONE,
          value: dealershipPhone,
          icon: <ChatIcon />,
        },
        {
          name: CHATTEL_FIELD_NAME.DEALERSHIP_EMAIL,
          value: dealershipEmail,
          icon: <ChatIcon />,
        },
      ],
    },
  ];
};
